/**
 * HelpArticlesTab
 * Admin management table for Help articles: grouped by category (ordered by
 * category sort_order, then article sort_order within each group).
 * Create/edit via GenericModal + ArticleForm, delete via ConfirmActionModal
 * (confirmation message shows the article title, AC 1053.2).
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2 } from 'lucide-react';
import type { UseFormSetError } from 'react-hook-form';
import { helpService } from '@services';
import { GenericModal } from '@components/common/modals';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import Button from '@components/common/Button';
import { showToast } from '@utils/toast';
import { getApiErrorMessage, getApiErrorStatus } from '@utils/apiError';
import { formatDate } from '@utils/dateUtils';
import type {
  HelpArticleWithCategory,
  CreateHelpArticlePayload,
  UpdateHelpArticlePayload
} from '@app-types';
import ArticleForm, { type ArticleFormValues } from './ArticleForm';
import styles from './HelpArticlesTab.module.css';

const HTTP_BAD_REQUEST = 400;
const NO_AUTHOR_PLACEHOLDER = '-';

const HelpArticlesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticleWithCategory | null>(null);

  // Held in refs (not state) so handing setError to the parent never triggers
  // a re-render of the form that produced it.
  const createSetErrorRef = useRef<UseFormSetError<ArticleFormValues> | null>(null);
  const editSetErrorRef = useRef<UseFormSetError<ArticleFormValues> | null>(null);

  const handleCreateSetErrorReady = useCallback((setError: UseFormSetError<ArticleFormValues>) => {
    createSetErrorRef.current = setError;
  }, []);

  const handleEditSetErrorReady = useCallback((setError: UseFormSetError<ArticleFormValues>) => {
    editSetErrorRef.current = setError;
  }, []);

  /**
   * Surfaces the failure as a toast and, for a 400 rejecting the body, also
   * pins it to the body field so the still-open modal shows what needs fixing.
   */
  const handleMutationError = (
    error: Error,
    fallback: string,
    setError: UseFormSetError<ArticleFormValues> | null
  ) => {
    const message = getApiErrorMessage(error, fallback);
    showToast.error(message);
    if (getApiErrorStatus(error) === HTTP_BAD_REQUEST) {
      setError?.('body', { type: 'server', message });
    }
  };

  const { data: categories = [] } = useQuery({
    queryKey: ['help-admin-categories'],
    queryFn: () => helpService.getAdminCategories()
  });

  const { data: articles = [], isLoading: articlesLoading } = useQuery({
    queryKey: ['help-admin-articles'],
    queryFn: () => helpService.getAdminArticles()
  });

  const categoriesById = useMemo(() => {
    const map = new Map<number, { id: number; name: string; sort_order: number }>();
    categories.forEach((category) => map.set(category.id, category));
    return map;
  }, [categories]);

  const groupedArticles = useMemo(() => {
    const byCategory = new Map<number, HelpArticleWithCategory[]>();
    articles.forEach((article) => {
      const list = byCategory.get(article.category_id) ?? [];
      list.push(article);
      byCategory.set(article.category_id, list);
    });

    const groups = Array.from(byCategory.entries()).map(([categoryId, categoryArticles]) => ({
      categoryId,
      categoryName: categoriesById.get(categoryId)?.name ?? categoryArticles[0]?.category_name ?? 'Uncategorized',
      categorySortOrder: categoriesById.get(categoryId)?.sort_order ?? 0,
      articles: [...categoryArticles].sort((a, b) => a.sort_order - b.sort_order)
    }));

    return groups.sort((a, b) => a.categorySortOrder - b.categorySortOrder);
  }, [articles, categoriesById]);

  const nextSortOrderForCategory = (categoryId: number | undefined): number => {
    if (categoryId === undefined) return 1;
    const inCategory = articles.filter((article) => article.category_id === categoryId);
    return inCategory.length > 0 ? Math.max(...inCategory.map((a) => a.sort_order)) + 1 : 1;
  };

  const invalidateArticles = () => {
    queryClient.invalidateQueries({ queryKey: ['help-admin-articles'] });
    // The reader queries (Help Center tab, HelpArticlePage) have no observer
    // while this tab is open, and the app disables refetch-on-mount, so a plain
    // invalidation would leave them serving pre-edit data until a reload.
    // refetchType 'all' refetches them now, inactive or not.
    queryClient.invalidateQueries({ queryKey: ['help-categories'], refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['help-articles'], refetchType: 'all' });
    // Prefix match: covers every cached ['help-article', id] detail.
    queryClient.invalidateQueries({ queryKey: ['help-article'], refetchType: 'all' });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateHelpArticlePayload) => helpService.createArticle(data),
    onSuccess: () => {
      showToast.success('Article created successfully');
      invalidateArticles();
      setIsCreateModalOpen(false);
    },
    onError: (error: Error) => {
      handleMutationError(error, 'Failed to create article', createSetErrorRef.current);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHelpArticlePayload }) => helpService.updateArticle(id, data),
    onSuccess: () => {
      showToast.success('Article updated successfully');
      invalidateArticles();
      setIsEditModalOpen(false);
    },
    onError: (error: Error) => {
      handleMutationError(error, 'Failed to update article', editSetErrorRef.current);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => helpService.deleteArticle(id),
    onSuccess: () => {
      showToast.success('Article deleted successfully');
      invalidateArticles();
      setIsDeleteModalOpen(false);
      setSelectedArticle(null);
    },
    onError: (error: Error) => {
      showToast.error(getApiErrorMessage(error, 'Failed to delete article'));
    }
  });

  const handleCreate = (data: CreateHelpArticlePayload | UpdateHelpArticlePayload) => {
    createMutation.mutate(data as CreateHelpArticlePayload);
  };

  const handleUpdate = (data: CreateHelpArticlePayload | UpdateHelpArticlePayload) => {
    if (!selectedArticle) return;
    updateMutation.mutate({ id: selectedArticle.id, data });
  };

  const handleDelete = () => {
    if (!selectedArticle) return;
    deleteMutation.mutate(selectedArticle.id);
  };

  if (articlesLoading) {
    return <div className={styles.loadingState}>Loading articles...</div>;
  }

  return (
    <div className={styles.articlesTab} data-test-id="help-articles-tab">
      <div className={styles.header}>
        <Button variant="primary" size="sm" onClick={() => setIsCreateModalOpen(true)} data-test-id="add-article-button">
          <Plus size={16} />
          Add Article
        </Button>
      </div>

      {groupedArticles.length === 0 ? (
        <div className={styles.emptyState}>No articles found</div>
      ) : (
        groupedArticles.map((group) => (
          <div key={group.categoryId} className={styles.categoryGroup} data-test-id={`article-group-${group.categoryId}`}>
            <div className={styles.categoryHeader}>
              <h3 className={styles.categoryName}>{group.categoryName}</h3>
              <span className={styles.categoryCount}>{group.articles.length}</span>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.table} data-test-id="help-articles-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Sort Order</th>
                    <th>Updated At</th>
                    <th>Updated By</th>
                    <th className={styles.actionsColumn}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                {group.articles.map((article) => {
                  const displayDate = article.updated_at ?? article.created_at;
                  // Names are resolved server-side; mirrors the updated -> created
                  // fallback used for the date.
                  const displayUserName =
                    article.updated_by_name ?? article.created_by_name ?? NO_AUTHOR_PLACEHOLDER;

                  return (
                    <tr key={article.id} data-test-id={`article-row-${article.id}`}>
                      <td className={styles.articleTitle}>{article.title}</td>
                      <td className={styles.mutedCell}>{article.sort_order}</td>
                      <td className={styles.mutedCell}>{formatDate(displayDate)}</td>
                      <td className={styles.mutedCell}>{displayUserName}</td>
                      <td className={styles.actionsColumn}>
                        <div className={styles.actionButtons}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedArticle(article);
                              setIsEditModalOpen(true);
                            }}
                            title="Edit"
                            data-test-id="action-button-edit"
                          >
                            <Edit size={16} color="#2563eb" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedArticle(article);
                              setIsDeleteModalOpen(true);
                            }}
                            title="Delete"
                            data-test-id="action-button-delete"
                          >
                            <Trash2 size={16} color="#dc2626" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <GenericModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New Article"
        size="lg"
        disableBackdropClick
        dataTestId="create-article-modal"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => formRef.current?.requestSubmit()}
              loading={createMutation.isPending}
              disabled={createMutation.isPending}
              data-test-id="submit-create-article"
            >
              Create Article
            </Button>
          </>
        }
      >
        <ArticleForm
          categories={categories}
          defaultSortOrder={nextSortOrderForCategory(categories[0]?.id)}
          onSubmit={handleCreate}
          formRef={formRef}
          isSubmitting={createMutation.isPending}
          onSetErrorReady={handleCreateSetErrorReady}
        />
      </GenericModal>

      <GenericModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Article"
        size="lg"
        disableBackdropClick
        dataTestId="edit-article-modal"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsEditModalOpen(false)} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => formRef.current?.requestSubmit()}
              loading={updateMutation.isPending}
              disabled={updateMutation.isPending}
              data-test-id="submit-edit-article"
            >
              Save Changes
            </Button>
          </>
        }
      >
        {selectedArticle && (
          <ArticleForm
            article={selectedArticle}
            categories={categories}
            defaultSortOrder={selectedArticle.sort_order}
            onSubmit={handleUpdate}
            formRef={formRef}
            isSubmitting={updateMutation.isPending}
            onSetErrorReady={handleEditSetErrorReady}
          />
        )}
      </GenericModal>

      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Article"
        message={`Are you sure you want to permanently delete "${selectedArticle?.title}"? This action cannot be undone.`}
        isSubmitting={deleteMutation.isPending}
        actionType="delete"
      />
    </div>
  );
};

export default HelpArticlesTab;
